import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Card, CardHeader, DateInput, Field, Input, Button } from '@truecare/ui';
import { AdminLayout } from '../components/AdminLayout';
import { AuditTrailPanel } from '../components/AuditTrailPanel';
import { adminBeforeLoad } from '../lib/admin-before-load';

/**
 * Phase 14-E + 14a-4 — global audit-event viewer.
 *
 * The filter form on this page is the global mode for `AuditTrailPanel`;
 * per-entity detail pages at 14b reuse the same component with `mode=
 * 'entity-scoped'` + fixed `entityType + entityId` filters.
 */
export const Route = createFileRoute('/audit')({
  beforeLoad: () => adminBeforeLoad('/audit'),
  component: AuditPage,
});

function AuditPage() {
  const [actorUserId, setActorUserId] = useState('');
  const [action, setAction] = useState('');
  const [entityType, setEntityType] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [applied, setApplied] = useState<{
    actorUserId?: string;
    action?: string;
    entityType?: string;
    fromDate?: string;
    toDate?: string;
    page: number;
    limit: number;
  }>({ page: 1, limit: 50 });

  return (
    <AdminLayout>
      <h1 className="font-heading text-3xl text-forest md:text-4xl">Audit events</h1>
      <p className="mt-1 text-sm text-ink-muted">
        A searchable record of every action taken across the platform, newest
        first. Filter by who did it, what happened, the record involved, or a
        date range.
      </p>

      <Card padding="md" className="mt-6">
        <CardHeader title="Filters" />
        <form
          className="grid grid-cols-1 gap-4 md:grid-cols-3"
          onSubmit={(e) => {
            e.preventDefault();
            setApplied({
              actorUserId: actorUserId || undefined,
              action: action || undefined,
              entityType: entityType || undefined,
              fromDate: fromDate || undefined,
              toDate: toDate || undefined,
              page: 1,
              limit: 50,
            });
          }}
        >
          <Field label="Actor user id">
            <Input
              value={actorUserId}
              onChange={(e) => setActorUserId(e.target.value)}
              placeholder="UUID"
            />
          </Field>
          <Field label="Action (substring)">
            <Input
              value={action}
              onChange={(e) => setAction(e.target.value)}
              placeholder="e.g. payout.cycle_close"
            />
          </Field>
          <Field label="Entity type">
            <Input
              value={entityType}
              onChange={(e) => setEntityType(e.target.value)}
              placeholder="e.g. payout"
            />
          </Field>
          <Field label="From">
            <DateInput value={fromDate} onChange={setFromDate} />
          </Field>
          <Field label="To">
            <DateInput value={toDate} onChange={setToDate} />
          </Field>
          <div className="md:col-span-3">
            <Button type="submit">Apply filters</Button>
          </div>
        </form>
      </Card>

      <div className="mt-6">
        <AuditTrailPanel mode="global" filters={applied} />
      </div>
    </AdminLayout>
  );
}
